import dayjs, { Dayjs } from 'dayjs'
import router from 'next/router'
import { memo, useCallback, useEffect, useRef, useState, VFC } from 'react'
import { useInView } from 'react-intersection-observer'
import {
  deleteMessage,
  deleteScheduledMessage,
  patchScheduledMessage,
  sendMessage,
  useMessages,
  useScheduledMessages,
} from 'src/api/message'
import {
  acceptInterested,
  rejectInterested,
  useRequestBox,
} from 'src/api/requestBox'
import { unhiddenRoom } from 'src/api/room'
import { useErrorMessageModal, useMessageModal } from 'src/hooks/useModal'
import { Message as MessageModel, ScheduledMessage } from 'src/models/Message'
import { Room } from 'src/models/Room'
import { LoginUser } from 'src/models/User'
import {
  _guidemessagebg,
  _messagesbg,
  _messagesecondary,
  truewhite,
} from 'src/styles/colors'
import { convertMessagePageQuqry } from 'src/utils/routes'
import styled, { css } from 'styled-components'
import { SWRInfiniteResponse } from 'swr/infinite'
import { useMessageContext } from '../MessageContext'
import { ImageViewerModal } from '../Modals/ImageViewerModal'
import { MessageDeleteModal } from '../Modals/MessageDeleteModal'
import { ScheduleMessageUpdateModal } from '../Modals/ScheduleMessageUpdateModal'
import { ScheduleUrlBannerCloseModal } from '../Modals/ScheduleUrlBannerCloseModal'
import { ScheduleUrlModal } from '../Modals/ScheduleUrlModal'
import { DeletedUserModalContent } from '../RoomsList/Rooms'
import { TabType } from '../SubMenu/TabList'
import { MESSAGE_HEADER_HEIGHT, MessageHeader } from './Header'
import { CounterMessage, MyMessage, MyScheduledMessage } from './Message'
import { ScheduleUrlRegistrationBanner } from './Message/ScheduleUrl'
import { MessageForm } from './MessageForm'
import { MessageUnhiddenForm } from './MessageUnhiddenForm'
import { RequestBoxComponent } from './RequestBox'
import { RequestBoxForm } from './RequestBoxForm'

const BANNER_DISPALYED_DAYS = 14

type MessageProps = {
  user: LoginUser
  userId: number
  showMessageAttention: boolean
  myScheduleUrl?: string
  isActiveScheduleUrl?: boolean
  scheduleUrlBannerLastClosedAt?: Dayjs
  toggleSubMenu: VoidFunction
  openReportUserModal: (id: number) => void
  mutateRooms: SWRInfiniteResponse<Room[], any>['mutate']
  isSubMenuOpen: boolean
  activeTab: TabType
  setTabType: (tabType: TabType) => void
  paid: boolean
  showProfile: (userId: number) => void
}

export const messageSorter = (a: MessageModel, b: MessageModel) => {
  if (a.sentAt.isAfter(b.sentAt)) return -1
  if (b.sentAt.isAfter(a.sentAt)) return 1
  return a.id > b.id ? -1 : 1
}

const scheduledMessageSorter = (a: ScheduledMessage, b: ScheduledMessage) => {
  if (a.scheduledAt.isAfter(b.scheduledAt)) return -1
  if (b.scheduledAt.isAfter(a.scheduledAt)) return 1
  return a.id > b.id ? -1 : 1
}

const MessageContent: VFC<MessageProps & { className?: string }> = memo(
  ({
    user,
    userId,
    showMessageAttention,
    myScheduleUrl,
    isActiveScheduleUrl,
    scheduleUrlBannerLastClosedAt,
    toggleSubMenu,
    openReportUserModal,
    mutateRooms,
    isSubMenuOpen,
    activeTab,
    setTabType,
    paid,
    showProfile,
    className,
  }) => {
    const scrollEl = useRef<HTMLDivElement>(null)
    const { open } = useErrorMessageModal()
    const { open: openMessageModal } = useMessageModal()

    const [previewImage, setPreviewImage] = useState('')
    const [deleting, setDeleting] = useState<MessageModel | ScheduledMessage>()
    const [updatingScheduled, setUpdatingScheduled] =
      useState<ScheduledMessage>()
    const [openScheduleUrlModal, setOpenScheduleUrlModal] = useState(false)
    const [
      openScheduleUrlBannerCloseModal,
      setOpenScheduleUrlBannerCloseModal,
    ] = useState(false)
    const { room, negativeBadges } = useMessageContext()
    const { data, error, mutate, setSize, size, isValidating } = useMessages(
      room?.url || '',
    )

    useEffect(() => {
      // NOTE: エラーモーダルを2重で表示しないようにする
      if (error && Array.isArray(error.errorMessages)) {
        open({ message: error.errorMessages.join('\n') })

        // NOTE: status: :trashed の削除済ルームを開いた場合はメッセージトップへリダイレクトさせる
        if (error.errorMessages.includes('ルームは削除されました'))
          router.replace(router.pathname, undefined, { shallow: true })
      } else if (room?.hasDeletedUser) openMessageModal(DeletedUserModalContent)
    }, [room?.id])

    const { data: requestBox, mutate: mutateRequestBox } = useRequestBox(
      room?.type === 'CompanyMessageRoom' ? room?.url : '',
    )
    // NOTE: ログイン中のユーザーがadminだった場合は承認せずにメッセージを送信できる
    const shouldHideInputForm = user.plan.isAdminRole
      ? false
      : !!(
          requestBox &&
          (requestBox.status === 'request' ||
            requestBox.status === 'be_rejected' ||
            requestBox.status === 'not_interest') &&
          requestBox.companyId !== userId
        ) ||
        room?.hasDeletedUser ||
        user.plan.isAdminRole

    const { data: scheduled, mutate: mutateScheduled } = useScheduledMessages(
      room?.type,
      room?.id,
    )

    const messages = data ? ([] as MessageModel[]).concat(...data) : []
    const images = messages
      .flatMap((msg) => msg.content.imageSources)
      .concat(scheduled?.flatMap((each) => each.content.imageSources))
      .filter(Boolean)
      .map((doc) => doc?.url!!)

    const unhiddenRoomCb = useCallback(
      (room: Room) =>
        unhiddenRoom(room.unhiddenUrl!!, room.uniqueId, userId, mutateRooms),
      [mutateRooms, userId],
    )
    const onInterested = useCallback(async () => {
      if (!requestBox?.companyId) return
      const { error } = await acceptInterested(requestBox.companyId)
      if (!error) {
        mutateRequestBox()
        mutateRooms()
      }
    }, [mutateRequestBox, mutateRooms, requestBox?.companyId])

    const onNotInterested = useCallback(async () => {
      if (!requestBox?.companyId) return
      const { error } = await rejectInterested(requestBox.companyId)
      if (!error) {
        await mutateRooms()
        router.replace({
          pathname: router.pathname,
          query: convertMessagePageQuqry({
            ...router.query,
            room_id: undefined,
          }),
        })
      }
    }, [mutateRooms, requestBox?.companyId])
    const twoWeeksAfterClosingBanner = () => {
      if (!scheduleUrlBannerLastClosedAt) return true
      const now = dayjs()
      return (
        now.diff(dayjs(scheduleUrlBannerLastClosedAt), 'day') >
        BANNER_DISPALYED_DAYS
      )
    }

    const { ref, inView } = useInView({})
    useEffect(() => {
      if (isValidating) return
      if (data && !data[size - 1]?.length) return
      if (inView) setSize(size + 1)
    }, [inView, isValidating, size, setSize, data])

    return (
      <StyledSection className={className}>
        <MessageHeader
          counters={room?.counters}
          toggleSubMenu={toggleSubMenu}
          openReportUserModal={openReportUserModal}
          isGroup={!!room?.isGroup}
          isReported={negativeBadges?.length > 0}
          feedbacked={room?.feedbacked || false}
          canFeedback={room?.canFeedback || false}
          hasDeletedUser={room?.hasDeletedUser || false}
          isSubMenuOpen={isSubMenuOpen}
          activeTab={activeTab}
          setTabType={setTabType}
          paid={paid}
          mutateRooms={mutateRooms}
          showProfile={showProfile}
        />
        {userId &&
          !myScheduleUrl &&
          isActiveScheduleUrl &&
          twoWeeksAfterClosingBanner() && (
            <ScheduleUrlRegistrationBanner
              open={() => setOpenScheduleUrlModal(true)}
              openBannerCloseModal={() =>
                setOpenScheduleUrlBannerCloseModal(true)
              }
            />
          )}
        <Scrollable ref={scrollEl}>
          {room && (
            <>
              {scheduled?.sort(scheduledMessageSorter).map((message) => (
                <MyScheduledMessage
                  key={message.id}
                  message={message}
                  onDelete={() => setDeleting(message)}
                  onUpdate={() => setUpdatingScheduled(message)}
                  setPreviewImage={setPreviewImage}
                />
              ))}
              {messages
                .sort(messageSorter)
                .map((message) =>
                  message.senderId === userId ? (
                    <MyMessage
                      key={message.uniqueId}
                      message={message}
                      onDelete={() => setDeleting(message)}
                      setPreviewImage={setPreviewImage}
                      isGroup={room.isGroup}
                    />
                  ) : (
                    <CounterMessage
                      key={message.uniqueId}
                      sender={
                        room.counters.find(
                          (counter) => counter.id === message.senderId,
                        )!!
                      }
                      message={message}
                      setPreviewImage={setPreviewImage}
                      onClickSender={() => showProfile(message.senderId)}
                    />
                  ),
                )}
              {requestBox && (
                <>
                  <RequestBoxComponent
                    requestBox={requestBox}
                    sender={
                      room.counters.find(
                        (counter) => counter.id === requestBox.companyId,
                      )!!
                    }
                    showProfile={showProfile}
                  />
                  {!room.isHidden && (
                    <RequestBoxForm
                      requestBox={requestBox}
                      sender={
                        room.counters.find(
                          (counter) => counter.id === requestBox.companyId,
                        )!!
                      }
                      onInterested={onInterested}
                      onNotInterested={onNotInterested}
                      isHidden={room.isHidden}
                    />
                  )}
                </>
              )}
              {room.triggerText?.receivedGuideMessageFlag && (
                <GuideMessage>
                  <div>つながりが成立しました！</div>
                  <div>プロフィールを見てメッセージを送りましょう</div>
                </GuideMessage>
              )}
              {room.triggerText?.sendGuideMessageFlag && (
                <GuideMessage>
                  <div>あなたがつながり申請を送ったユーザーと</div>
                  <div>つながりが成立しました！</div>
                  <div>プロフィールを見てメッセージを送りましょう</div>
                </GuideMessage>
              )}
              {room.triggerText?.matchingDateText && (
                <MatchingMessage>
                  {room.triggerText.matchingDateText}
                </MatchingMessage>
              )}
              {room.triggerText.matchingDetailText && (
                <MatchingMessage>
                  {room.triggerText.matchingDetailText}
                </MatchingMessage>
              )}
            </>
          )}
          <div ref={ref} />
        </Scrollable>
        {room &&
          (!room.isHidden ? (
            <>
              {!shouldHideInputForm && (
                <MessageForm
                  postMessage={async (content: {
                    text?: string
                    files?: File[]
                    scheduledAt?: Dayjs
                    noAttention?: boolean
                  }) => {
                    const res = await sendMessage(
                      {
                        id: room.id,
                        roomType: room.type,
                        url: room.url,
                        content: content.text,
                        companyDocuments: content.files,
                        scheduledAt: content.scheduledAt?.toDate(),
                        noAttention: content.noAttention,
                      },
                      content.scheduledAt ? mutateScheduled : mutate,
                    )
                    const el = scrollEl.current
                    if (el)
                      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
                    return res
                  }}
                  requestBox={requestBox || undefined}
                  sentRequest={userId == requestBox?.companyId}
                  myScheduleUrl={myScheduleUrl}
                  showMessageAttention={showMessageAttention}
                />
              )}
            </>
          ) : (
            <MessageUnhiddenForm
              unhiddenRoom={async () => {
                const res = await unhiddenRoomCb(room)
                if (res.error)
                  open({ message: res.error.errorMessages?.join('\n') })
              }}
            />
          ))}
        {previewImage && (
          <ImageViewerModal
            images={images}
            initialImage={previewImage}
            close={() => setPreviewImage('')}
          />
        )}
        {deleting && (
          <MessageDeleteModal
            execDelete={() => {
              if ('scheduledAt' in deleting)
                return deleteScheduledMessage(deleting.id, mutateScheduled)
              return deleteMessage(
                deleting.destroyUrl,
                deleting.uniqueId,
                mutate,
              )
            }}
            close={() => setDeleting(undefined)}
          />
        )}
        {updatingScheduled && (
          <ScheduleMessageUpdateModal
            text={updatingScheduled.content.text!!}
            update={(text) =>
              patchScheduledMessage(updatingScheduled.id, text, mutateScheduled)
            }
            close={() => setUpdatingScheduled(undefined)}
          />
        )}
        {openScheduleUrlModal && (
          <ScheduleUrlModal
            id={'scheduleUrl'}
            close={() => setOpenScheduleUrlModal(false)}
            mutateRequestBox={mutateRequestBox}
          />
        )}
        {openScheduleUrlBannerCloseModal && (
          <ScheduleUrlBannerCloseModal
            close={() => setOpenScheduleUrlBannerCloseModal(false)}
          />
        )}
      </StyledSection>
    )
  },
)

export const MessageComponent = styled(MessageContent)`
  padding-right: 6px;
  background-color: ${_messagesbg};
`

const Scrollable = styled.div`
  display: flex;
  flex-direction: column-reverse;
  overflow-y: auto;
  height: calc(100% - ${MESSAGE_HEADER_HEIGHT});
`
const MatchingMessage = styled.p`
  width: 100%;
  display: flex;
  align-items: center;
  text-align: center;
  ::before,
  ::after {
    content: '';
    border-top: 1px solid ${_messagesecondary};
    flex: 1;
    margin: 0 3em;
  }
`

const sectionGuide = css`
  padding-right: 16px;
  padding-left: 40px;
`
const StyledSection = styled.section`
  position: relative;

  ${MessageHeader} {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    ${sectionGuide};
  }
  ${ScheduleUrlRegistrationBanner} {
    position: absolute;
    top: ${MESSAGE_HEADER_HEIGHT};
    right: 0;
    left: 0;
  }
  ${Scrollable} {
    padding-top: ${MESSAGE_HEADER_HEIGHT};
    padding-bottom: 240px;
    @media screen and (max-width: 960px) {
      padding-bottom: 140px;
    }
    ${sectionGuide};
    > * {
      margin-bottom: 32px;
      /* NOTE: OS-1073 safari v13 で表示崩れが起きてメッセージとヘッダーが重なるため余白を追加 */
      :last-child {
        padding-top: ${MESSAGE_HEADER_HEIGHT};
      }
    }
  }
  ${MessageForm},${MessageUnhiddenForm} {
    position: absolute;
    bottom: 16px;
    right: 16px;
    left: 40px;
  }
  ${RequestBoxForm} {
    position: absolute;
    margin: 0px;
    bottom: 0px;
    right: 0px;
    left: 0px;
  }
`
const GuideMessage = styled.div`
  width: 350px;
  margin-left: auto;
  margin-right: auto;
  padding: 16px;
  border-radius: 10px;
  text-align: center;
  color: ${truewhite};
  background-color: ${_guidemessagebg};
`
